import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, X, Layers, FileText } from "lucide-react";

const sectionOptions = [
  { value: "GENERAL", label: "General / Other Sector" },
  { value: "ELEMENTARY", label: "Elementary School Division" },
  { value: "HIGH_SCHOOL", label: "High School Division" },
];

export default function RoomModal({ isOpen, onClose, onSave, room }) {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [section, setSection] = useState("GENERAL"); 
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (room) {
      setCode(room.code || "");
      setDescription(room.description || "");
      setSection(room.section || "GENERAL");
    } else {
      setCode("");
      setDescription("");
      setSection("GENERAL");
    }
    setIsDropdownOpen(false);
  }, [room, isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanPayload = {
      code: code.trim(),
      description: description ? description.trim() : "", 
      section: section ? section.toUpperCase().trim() : "GENERAL", 
    };
    onSave(cleanPayload);
  };
  
  const currentSelectionLabel = sectionOptions.find(opt => opt.value === section)?.label || "Select section...";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          {/* Modal Card */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-md shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-hidden"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

            {/* Header section */}
            <div className="p-5 flex justify-between items-start bg-slate-50/50 border-b border-slate-100">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm tracking-tight flex items-center gap-2">
                  <Layers size={15} className="text-[#002855]" />
                  {room ? "Modify Zone Configuration" : "Provision New Zone Node"}
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Set structural identification metrics and access mappings.
                </p>
              </div>
              <button 
                type="button"
                onClick={onClose} 
                className="p-1.5 hover:bg-slate-200/60 rounded-lg text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Form workspace */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4 text-sm">
              
              {/* Room Code field */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Room Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  placeholder="e.g., EL 201"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] bg-slate-50/50 focus:bg-white transition-all placeholder-slate-400"
                />
              </div>

              {/* Custom Dropdown option selection component */}
              <div className="space-y-1.5" ref={dropdownRef}>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Education Division Section
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-3 py-2 text-xs text-slate-800 outline-none flex items-center justify-between transition-all hover:bg-white focus:border-[#002855] focus:ring-2 focus:ring-[#002855]/10 font-medium text-left cursor-pointer shadow-xs"
                  >
                    <span>{currentSelectionLabel}</span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? "rotate-180 text-slate-600" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div 
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 mt-1.5 w-full bg-white border border-slate-200/80 rounded-xl shadow-xl z-50 p-1"
                      >
                        {sectionOptions.map((option) => {
                          const isSelected = section === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setSection(option.value);
                                setIsDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-xs rounded-lg font-medium text-left transition-colors cursor-pointer ${
                                isSelected 
                                  ? "bg-[#002855]/5 text-[#002855] font-semibold" 
                                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                              }`}
                            >
                              <span>{option.label}</span>
                              {isSelected && <Check size={13} className="text-[#002855] stroke-[2.5]" />}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Text Description area */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  <FileText size={12} className="text-slate-400" /> Supplemental Details
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide context about equipment access, structure properties..."
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] bg-slate-50/50 focus:bg-white transition-all h-24 resize-none placeholder-slate-400"
                />
              </div>

              {/* Controls block footer */}
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 mt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 hover:bg-slate-100 rounded-xl border border-slate-200 bg-white text-slate-600 font-medium text-xs transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl font-medium text-xs transition-all shadow-sm cursor-pointer active:scale-98"
                >
                  Commit Change
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}